import type { WebsiteContent } from './website';
import type { IntegrationConnection } from './integrations';
import type { TeamMember } from './teamAccess';

export type LaunchStatus = 'ready' | 'attention' | 'manual';

export interface LaunchCheck {
  label: string;
  status: LaunchStatus;
  detail: string;
}

export interface LaunchStep {
  id: string;
  number: number;
  title: string;
  timing: string;
  summary: string;
  /** Numbered instructions written for someone who has never done this before. */
  instructions: string[];
  /** Optional outside link the step depends on. */
  linkLabel?: string;
  linkUrl?: string;
  /** Where inside the studio this work happens. */
  studioPath?: string;
  studioLabel?: string;
  /** Honest warning shown in amber. */
  warning?: string;
  /** Checks measured against real app state. Empty when nothing can be verified here. */
  checks: (context: LaunchContext) => LaunchCheck[];
}

export interface LaunchContext {
  content: WebsiteContent;
  connections: IntegrationConnection[];
  team: TeamMember[];
}

const filled = (value: string) => value.trim().length > 0;

function check(label: string, ok: boolean, okDetail: string, gapDetail: string): LaunchCheck {
  return { label, status: ok ? 'ready' : 'attention', detail: ok ? okDetail : gapDetail };
}

export const launchSteps: LaunchStep[] = [
  {
    id: 'content',
    number: 1,
    title: 'Fill in your real content',
    timing: 'About an hour',
    summary:
      'Everything on the public site is edited in one place. Work down the cards in Website Studio and replace anything that is still a placeholder.',
    studioPath: '/studio/website',
    studioLabel: 'Open Website Studio',
    instructions: [
      'Open Website Studio from the sidebar.',
      'Homepage — band name, main photo, headline, and the short description under it.',
      'Social links — TikTok, Instagram, Spotify, YouTube. Paste the full web address for each.',
      'Music — section photo, headline, and your Spotify and Apple Music links. A blank link hides that button.',
      'Merch store — real products with real checkout links, or untick Show on site until you have them.',
      'Images from the Road — your photos, each with a caption naming the venue and city.',
      'About page — story, band members, venue credits, awards, artist quotes, and the three photos.',
      'Contact — the booking email address that actually reaches you.',
      'Search & sharing — page title, description, city, genre, and set the site address to https://coopertyband.com.',
      'Click Publish at the top of Website Studio when you are happy.',
    ],
    warning:
      'Uploaded photos now go to your own cloud storage, so there is no size limit and they appear on every device. The Website Studio cloud card tells you whether your latest edit reached the database.',
    checks: ({ content }) => [
      check(
        'Homepage photo and description',
        filled(content.heroImageUrl) && filled(content.heroDescription),
        'Hero image and description are set.',
        'Add a hero photo and the short description underneath it.',
      ),
      check(
        'Streaming links',
        filled(content.musicPrimaryUrl) || filled(content.musicSecondaryUrl),
        'At least one streaming button is live.',
        'Add your Spotify or Apple Music link in the Music card.',
      ),
      check(
        'Booking email',
        filled(content.bookingEmail),
        `Booking goes to ${content.bookingEmail}.`,
        'Add the booking email address so venues can reach you.',
      ),
      check(
        'Site address set to your domain',
        content.siteUrl.includes('coopertyband.com'),
        'Search & sharing points at coopertyband.com.',
        'Set the website address to https://coopertyband.com in Search & sharing.',
      ),
      check(
        'About page written',
        content.aboutBody.trim().length > 400 && content.aboutMembers.length > 0,
        'The story and band members are in place.',
        'Fill out the About page story and name the band members.',
      ),
    ],
  },
  {
    id: 'bandsintown',
    number: 2,
    title: 'Connect Bandsintown',
    timing: 'About 15 minutes',
    summary:
      'This is what keeps your tour dates current without you touching the site. Once auto-sync is on, adding a show in Bandsintown puts it on your website automatically.',
    linkLabel: 'Bandsintown for Artists',
    linkUrl: 'https://artists.bandsintown.com',
    studioPath: '/studio/website',
    studioLabel: 'Open Tour dates',
    instructions: [
      'Sign in to your artist account at artists.bandsintown.com.',
      'Find the developer or API section and request an App ID. It is free and usually instant.',
      'In Website Studio, open the Tour dates card.',
      'Enter your Bandsintown artist name exactly as it appears on your profile, and paste the App ID.',
      'Click Sync now. Your upcoming shows should appear with venue, city, date, and ticket link.',
      'Turn on Auto-sync on public site.',
      'Click Publish.',
    ],
    checks: ({ content }) => [
      check(
        'Artist name and App ID entered',
        filled(content.bandsintownArtist) && filled(content.bandsintownAppId),
        `Syncing as ${content.bandsintownArtist}.`,
        'Add your Bandsintown artist name and App ID in the Tour dates card.',
      ),
      check(
        'Shows published',
        content.shows.length > 0,
        `${content.shows.length} show${content.shows.length === 1 ? '' : 's'} on the site.`,
        'No tour dates published yet — run Sync now, or add a show by hand.',
      ),
      check(
        'Auto-sync turned on',
        content.bandsintownAutoSync,
        'The public site pulls dates from Bandsintown on every visit.',
        'Turn on Auto-sync so new Bandsintown dates appear without publishing.',
      ),
      check(
        'Every date has a ticket link',
        content.shows.length > 0 && content.shows.every((show) => filled(show.ticketUrl)),
        'All published dates link to tickets.',
        'Some dates have no ticket link — fans hit a dead end and Google ignores the event.',
      ),
    ],
  },
  {
    id: 'email',
    number: 3,
    title: 'Set up MailerLite to email your fans',
    timing: 'About 30 minutes, plus a wait for domain verification',
    summary:
      'VIP signups already reach you — they save to your database and appear in Fans & VIP. This step is about being able to email that list, and sending every new fan an automatic welcome.',
    linkLabel: 'MailerLite',
    linkUrl: 'https://www.mailerlite.com',
    studioPath: '/studio/settings',
    studioLabel: 'Open Integrations',
    instructions: [
      'Create a free account at mailerlite.com. The free tier covers 1,000 subscribers.',
      'Create a group called VIP. This is your fan list.',
      'Build a signup form for that group asking for first name, email, and optionally mobile number.',
      'Turn on a welcome automation — one email sent automatically when someone joins. Write it in your own voice.',
      'Open their domain settings and verify coopertyband.com as your sending domain.',
      'Copy the DNS records MailerLite gives you. You will add them at GoDaddy in Step 5.',
      'Back in Settings, connect the email platform under "Actually send my fan emails and texts".',
    ],
    warning:
      'Integrations currently records that you connected something, but the live data connection still has to be built once your account exists. Ask for that as a follow-up. Until then, embed MailerLite\u2019s own form or send fans to a MailerLite landing page. Skip SMS at launch — texting has stricter consent rules and needs a separate provider.',
    checks: ({ connections }) => [
      check(
        'Email platform connected',
        connections.some((item) => item.goalId === 'email'),
        'An email platform is recorded in Integrations.',
        'No email platform connected — you can collect fans, but not email them yet.',
      ),
    ],
  },
  {
    id: 'publish',
    number: 4,
    title: 'Publish and test before touching the domain',
    timing: 'About 30 minutes',
    summary:
      'Publishing gives your site a temporary web address. Nothing about coopertyband.com changes yet, so this is the safe place to find problems.',
    instructions: [
      'Click PUBLISH in the top right of the Magic Patterns editor.',
      'Upload your favicon in the same flow — the tiny logo in the browser tab.',
      'Open the temporary address in a private browsing window, on both a phone and a computer.',
      'Check the homepage photo is not cut off and every navigation link goes where it should.',
      'Check tour dates show real shows and each ticket link opens the right page.',
      'Check the Spotify and Apple Music buttons, and every merch checkout link.',
      'Open the About article, read it, and close it with the ✕ or Esc.',
      'Swipe the photo carousel on your phone, and tap the booking email link.',
      'Confirm nothing still says Lorem, Example, or shows a placeholder photo.',
      'Send the link to two people who were not involved and ask them to find your next show and your music.',
    ],
    checks: () => [
      { label: 'Tested on a phone and a computer', status: 'manual', detail: 'Only you can confirm this one.' },
      { label: 'Favicon uploaded in the Publish flow', status: 'manual', detail: 'Done outside the studio.' },
    ],
  },
  {
    id: 'domain',
    number: 5,
    title: 'Point coopertyband.com at the new site',
    timing: '15 minutes of work, then up to 24 hours of waiting',
    summary:
      'This feels scary and is not. You are changing a signpost, not moving anything. Do it on a weekday morning, never the night before a show.',
    linkLabel: 'GoDaddy',
    linkUrl: 'https://dcc.godaddy.com/control/portfolio',
    instructions: [
      'In the Magic Patterns Publish flow, choose Custom domain and enter coopertyband.com. Leave that screen open.',
      'It shows the records you need — usually an A record for the bare domain and a CNAME for the www version.',
      'Sign in at godaddy.com, open My Products, find coopertyband.com, and click Manage DNS.',
      'Screenshot the DNS page before changing anything. That screenshot is your undo button.',
      'Edit the existing records pointing at Bandzoogle — usually an A record named @ and a CNAME named www.',
      'Do not delete anything else, especially MX records, which handle your email.',
      'Add the DNS records MailerLite gave you in Step 3 as new entries. They will not conflict.',
      'Save, then wait. Usually 15 minutes to 2 hours, occasionally up to 24.',
      'Once the new site loads, confirm the domain shows as verified and the padlock appears in the address bar.',
      'Check that www.coopertyband.com works too.',
    ],
    warning:
      'Do not cancel Bandzoogle yet. Keep it paid through launch week — if anything goes wrong you can point the domain back in minutes, but only while that account is active. During the wait, some people may see the old site and others the new one. That is normal.',
    checks: () => [
      { label: 'DNS page screenshotted first', status: 'manual', detail: 'Your rollback plan.' },
      { label: 'Domain verified with a padlock', status: 'manual', detail: 'Confirmed in the Publish flow.' },
    ],
  },
  {
    id: 'google',
    number: 6,
    title: 'Tell Google the site exists',
    timing: 'About 30 minutes',
    summary:
      'Search Visibility has the full checklist with links and tick boxes. Search Console is the single highest-value item — it proves you own the site and asks Google to index it.',
    studioPath: '/studio/search',
    studioLabel: 'Open Search Visibility',
    linkLabel: 'Google Search Console',
    linkUrl: 'https://search.google.com/search-console',
    instructions: [
      'Open Search Visibility in the sidebar and clear anything flagged under Site health.',
      'Work through Setup steps outside the site — each has a link and remembers your progress.',
      'Verify the site in Google Search Console and request indexing.',
      'Claim or create your Google Business Profile so the panel appears beside your name in search.',
      'Add coopertyband.com to Spotify, Apple Music, Bandsintown, YouTube, and every social bio.',
      'Ask venues and local press to link your site when they list your shows.',
    ],
    warning:
      'There is no sitemap.xml file yet — Search Console will ask for one. Ask for it as a small follow-up. For a site this size you can submit pages individually in the meantime. Expect a few days to be found, two to four weeks to rank for "Cooper Ty Band", and months for competitive local searches.',
    checks: ({ content }) => [
      check(
        'Search title and description written',
        filled(content.seoTitle) && filled(content.seoDescription),
        'Your Google result is written.',
        'Fill in the page title and search description in Search Visibility.',
      ),
      check(
        'Share image set',
        filled(content.shareImageUrl),
        'Shared links show a photo.',
        'Add a link preview image so shared links are not blank.',
      ),
      { label: 'Verified in Search Console', status: 'manual', detail: 'Tracked in Search Visibility.' },
    ],
  },
  {
    id: 'access',
    number: 7,
    title: 'Set up who can log in',
    timing: '10 minutes',
    summary:
      'Settings holds the list of people allowed into the management area. Add each person with their name, email, mobile, and role. Owners cannot be removed; managers can be removed the moment someone leaves.',
    studioPath: '/studio/settings',
    studioLabel: 'Open Who can log in',
    instructions: [
      'Open Settings, then Who can log in.',
      'Add each bandmate or manager with name, email, mobile number, and role.',
      'Anyone not on this list is rejected at the login screen.',
      'Signing in: go to coopertyband.com/studio, enter your email, and tap the link that arrives.',
      'A session lasts 30 days before you need a new link.',
    ],
    warning:
      'Sign-in links are delivered by your Supabase project, so the studio is genuinely protected — only someone who controls an email address on the access list can get in. The Continue without signing in shortcut appears only while building and is automatically gone on the published site.',
    checks: ({ team }) => [
      check(
        'Team list set up',
        team.length > 1,
        `${team.length} people can sign in.`,
        'Only one person is on the list — add your bandmates and manager.',
      ),
    ],
  },
  {
    id: 'bandzoogle',
    number: 8,
    title: 'Retire Bandzoogle',
    timing: '10 minutes, after a full week live',
    summary:
      'Wait a full week with the new site working before you cancel anything. Your old mailing list is the most valuable thing on that account.',
    instructions: [
      'Log into Bandzoogle and export your mailing list.',
      'Export any photos or written content that only exists there.',
      'Confirm the exported list file opens and has real addresses in it.',
      'Import that list into your MailerLite VIP group.',
      'Confirm coopertyband.com still loads the new site correctly.',
      'Cancel the Bandzoogle subscription.',
    ],
    checks: () => [
      { label: 'Mailing list exported and imported', status: 'manual', detail: 'Do this before cancelling.' },
    ],
  },
];

export interface MaintenanceTask {
  trigger: string;
  action: string;
}

export const maintenanceTasks: MaintenanceTask[] = [
  { trigger: 'New show announced', action: 'Add it in Bandsintown. It appears on your site automatically — nothing else to do.' },
  { trigger: 'New single or album', action: 'Website Studio → Music. Update the photo, headline, and streaming links, then Publish.' },
  { trigger: 'New photos', action: 'Website Studio → Images from the Road. Add captions naming the venue and city, then Publish.' },
  { trigger: 'New merch', action: 'Website Studio → Merch store. Product, photo, price, checkout link, then Publish.' },
  { trigger: 'New award or venue credit', action: 'Website Studio → About page. These are what make you findable — add them as they happen.' },
  { trigger: 'Emailing fans', action: 'Write and send from MailerLite. The studio shows what is planned; MailerLite does the sending.' },
];

export const maintenanceRhythm: string[] = [
  'After every show — add two or three photos with captions naming the venue and city.',
  'Monthly — open Search Visibility and clear anything flagged. Check that ticket links still work.',
  'Every release — update the Music section, then email the VIP list from MailerLite.',
  'Quarterly — reread the About page. Add new venues, new credits, new quotes.',
];

export interface LaunchTruth {
  heading: string;
  items: string[];
}

export const launchTruths: LaunchTruth[] = [
  {
    heading: 'Working now',
    items: [
      'The public website and the About article',
      'Website Studio editing and publishing',
      'Bandsintown sync, including live auto-sync',
      'Search tags, social share previews, and structured event data',
      'The Search Visibility checklists',
      'Your database — content, photos, and fan signups are shared across every device',
      'Emailed sign-in links for the studio',
      'VIP signups reaching you in Fans & VIP',
    ],
  },
  {
    heading: 'Needs an outside service',
    items: [
      'Sending fan emails and the automatic welcome — needs MailerLite',
      'Streaming numbers, followers, and revenue in Mission Control',
    ],
  },
  {
    heading: 'Needs a follow-up build',
    items: [
      'Signups flowing automatically into MailerLite rather than being exported',
      'A sitemap.xml file for Search Console',
    ],
  },
];

export interface Troubleshoot {
  problem: string;
  fix: string;
}

export const troubleshooting: Troubleshoot[] = [
  {
    problem: 'The domain still shows the old site',
    fix: 'Normal for up to 24 hours. Try a private window or your phone on mobile data. After a day, recheck the GoDaddy records against what Publish showed you.',
  },
  {
    problem: 'The domain shows an error',
    fix: 'Restore the records from your screenshot to go back to Bandzoogle, then sort it out without pressure.',
  },
  {
    problem: 'No security padlock in the address bar',
    fix: 'The certificate can take a few hours after the domain resolves. If it is still missing the next day, check the domain status in the Publish flow.',
  },
  {
    problem: 'Tour dates are empty or stale',
    fix: 'Check the artist name in Website Studio matches Bandsintown exactly and the App ID is still valid. Use Sync now to test.',
  },
  {
    problem: 'A photo will not upload',
    fix: 'Check the storage meter in the gallery card. If it is near full, remove a photo or switch to hosted image links.',
  },
  {
    problem: 'Emails land in spam',
    fix: 'The MailerLite domain verification records at GoDaddy are missing or have not propagated. Recheck them in MailerLite\u2019s domain settings.',
  },
];

export const launchProgressKey = 'artistos-launch-progress';

export function getLaunchProgress(): string[] {
  try {
    const stored = window.localStorage.getItem(launchProgressKey);
    const parsed: unknown = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

export function saveLaunchProgress(ids: string[]): void {
  try {
    window.localStorage.setItem(launchProgressKey, JSON.stringify(ids));
  } catch {
    // Progress tracking is a convenience only.
  }
}
